import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';
import NatureMultiSelect from './NatureMultiSelect';
import api from '../Utils/api';
import { queryNatures, normalizeNature, natureOption, clearNatureQueries } from '../Utils/naturezaCatalog';
import { getNatureCatalog, putNatureCatalog, offlineDraftsEnabled } from '../offline/draftStore';

export default function OccurrenceNatureSelect({ boletim, setBoletim, onFieldChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [known, setKnown] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [offline, setOffline] = useState(false);
  const [more, setMore] = useState(false);
  const [retry, setRetry] = useState(0);
  const request = useRef(null);
  const generation = useRef(0);
  const page = useRef(1);
  const busy = useRef(false);
  const local = useRef([]);
  const ids = boletim.naturezaIds?.length ? boletim.naturezaIds.map(String) : boletim.naturezaId ? [String(boletim.naturezaId)] : [];
  const idsKey = ids.join(',');

  useEffect(() => {
    let active = true;
    getNatureCatalog().then(items => { if (active) { local.current = items || []; setKnown(current => [...new Map([...(items || []), ...current].map(item => [item.value, item])).values()]); } }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const missing = idsKey.split(',').filter(id => /^[a-f0-9]{24}$/i.test(id));
    if (!missing.length) return () => controller.abort();
    Promise.allSettled(missing.map(id => api.get(`/naturezas/${id}`, { signal: controller.signal }))).then(results => {
      if (active) setKnown(current => {
        const map = new Map(current.map(item => [item.value, item]));
        results.forEach(result => { if (result.status === 'fulfilled' && result.value.data?._id) { const item = natureOption(result.value.data); map.set(item.value, item); } });
        return [...map.values()];
      });
    });
    return () => { active = false; controller.abort(); };
  }, [idsKey]);

  const fetchPage = async (number, revision) => {
    if (busy.current) return;
    busy.current = true;
    const controller = new AbortController();
    request.current = controller;
    setLoading(true); setError('');
    try {
      if (navigator.onLine === false) throw new Error('offline');
      const data = await queryNatures(query, number, controller.signal);
      if (controller.signal.aborted || revision !== generation.current) return;
      const next = (data.items || []).map(natureOption);
      setOptions(current => number === 1 ? next : [...new Map([...current, ...next].map(item => [item.value, item])).values()]);
      setMore(Boolean(data.hasMore)); page.current = number; setOffline(false);
      const merged = new Map([...local.current, ...next].map(item => [item.value, item]));
      local.current = [...merged.values()].slice(-500);
      setKnown(current => [...new Map([...current, ...next].map(item => [item.value, item])).values()]);
      putNatureCatalog(local.current).catch(() => {});
    } catch (failure) {
      if (controller.signal.aborted || revision !== generation.current) return;
      const matches = local.current.filter(item => item.ativo && normalizeNature(item.nome || item.label).includes(normalizeNature(query)));
      setOptions(matches.slice(0, number * 20)); setMore(matches.length > number * 20); page.current = number;
      setOffline(true); setError('Sem conexão com o catálogo. As seleções e o rascunho foram preservados.');
    } finally {
      if (revision === generation.current) { busy.current = false; setLoading(false); }
    }
  };

  useEffect(() => {
    request.current?.abort(); busy.current = false;
    const revision = ++generation.current;
    setMore(false); setOptions([]); setLoading(false);
    if (!open || (normalizeNature(query).length > 0 && normalizeNature(query).length < 2)) return undefined;
    const timer = setTimeout(() => fetchPage(1, revision), query ? 300 : 0);
    return () => { clearTimeout(timer); request.current?.abort(); };
    // fetchPage intentionally captures the current query; generation rejects stale responses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, retry]);

  const names = String(boletim.natureza || '').split(';').map(name => name.trim()).filter(Boolean);
  const selected = ids.length ? ids.map((id, i) => known.find(item => item.value === id) ||
    { value: id, nome: boletim.naturezaSugestoes?.find(item => item.id === id)?.nome || names[i] || id,
      label: boletim.naturezaSugestoes?.find(item => item.id === id)?.nome || names[i] || id, ativo: true }) :
    names.map((nome, i) => ({ value: `legacy:${i}`, nome, label: nome, legado: true, ativo: false }));
  if (ids.length) (boletim.naturezaLegadas || []).forEach((nome, i) => selected.push({ value: `legacy:${i}`, nome, label: nome, legado: true, ativo: false }));
  const change = values => {
    const unique = [...new Map(values.map(item => [normalizeNature(item.nome || item.label), item])).values()];
    const references = unique.filter(item => !item.legado).map(item => item.value);
    onFieldChange?.();
    setBoletim(prev => ({ ...prev, naturezaIds: references, naturezaId: references.length === 1 ? references[0] : '',
      natureza: unique.map(item => item.nome || item.label).join('; '),
      naturezaLegadas: unique.filter(item => item.legado).map(item => item.nome),
      naturezaSugestoes: (prev.naturezaSugestoes || []).filter(item => references.includes(item.id)) }));
  };
  const create = async name => {
    if (creating) return;
    const nome = name.trim().replace(/\s+/g, ' ');
    if (!nome || nome.length > 500 || /[<>;]/.test(nome) || [...name].some(char => char.charCodeAt(0) < 32)) { setError('Informe um nome válido de até 500 caracteres.'); return; }
    setCreating(true); setError('');
    try {
      const { data } = await api.post('/naturezas/sugerir', { nome });
      if (!data.ativo) { setError('Já existe uma natureza equivalente inativa. Solicite revisão administrativa.'); return; }
      const option = natureOption(data);
      setKnown(current => [...current, option]); change([...selected, option]); clearNatureQueries();
    } catch (failure) {
      if (failure.response || !offlineDraftsEnabled) { setError(failure.response?.data?.message || 'Não foi possível cadastrar. Tente novamente.'); return; }
      const id = `local:${uuid()}`;
      onFieldChange?.();
      setBoletim(prev => ({ ...prev, naturezaIds: [...ids, id], naturezaId: '',
        natureza: [...names, nome].join('; '), naturezaLegadas: selected.filter(item => item.legado).map(item => item.nome),
        naturezaSugestoes: [...(prev.naturezaSugestoes || []), { id, nome }] }));
      setOffline(true);
    } finally { setCreating(false); }
  };

  return <>
    <NatureMultiSelect inputId="natureza-ocorrencia" ariaLabel="Natureza da ocorrência" value={selected}
      options={options} onChange={change} showActions={false} showCheckboxes={false} summarizeMultiple={false}
      isLoading={loading || creating} isDisabled={creating} remote onMenuOpen={() => setOpen(true)}
      onInputChange={(value, meta) => { if (['input-change', 'menu-close', 'set-value'].includes(meta.action)) setQuery(value); }}
      isOptionDisabled={item => !item.ativo}
      onCreateOption={create} formatCreateLabel={name => `+ Cadastrar nova natureza: ${name}`}
      isValidNewOption={name => normalizeNature(name).length >= 2 && !loading && ![...options, ...selected].some(item => normalizeNature(item.nome || item.label) === normalizeNature(name))}
      placeholder="Pesquise ou cadastre uma natureza"
      noOptionsMessage={() => normalizeNature(query).length === 1 ? 'Digite pelo menos 2 caracteres.' : 'Nenhuma natureza encontrada.'} />
    {more && <Button type="button" size="sm" variant="link" disabled={loading} onClick={() => fetchPage(page.current + 1, generation.current)}>Carregar mais resultados</Button>}
    {error && <Alert variant="warning" className="mt-2">{error} <Button type="button" size="sm" onClick={() => setRetry(value => value + 1)}>Tentar novamente</Button></Alert>}
    {offline && <p role="status">Catálogo local em uso. Sugestões locais aguardam confirmação do servidor.</p>}
    {boletim.naturezaSugestoes?.length > 0 && <p role="status">Sugestão salva neste rascunho; ainda não cadastrada no servidor.</p>}
  </>;
}
